import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { authFetch } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Paperclip, X, Download, Trash2, CheckSquare, Square, Mail, FolderOpen,
} from "lucide-react";
import {
  useRaccoglitore, chiaveVoce, togliDalRaccoglitore, svuotaRaccoglitore,
} from "@/lib/raccoglitore";

// Le etichette dei generi, per la riga sotto il titolo del documento.
const DA_DOVE = {
  dipendente: "fascicolo",
  cantiere: "cantiere",
  mezzo: "mezzo",
  azienda: "azienda",
  sgi: "sistemi di gestione",
};

/**
 * Il raccoglitore, la linguetta sul bordo destro che si apre e si chiude.
 *
 * Si gira per l'app con questo aperto di fianco: ogni documento ha la sua
 * graffetta, e quello che serve finisce qui. Alla fine si scarica tutto in una
 * cartella sola, divisa per proprietario.
 *
 * Due dettagli che sembrano capricci e non lo sono:
 *
 *  - Niente velo dietro il pannello. Il raccoglitore si riempie mentre si
 *    lavora, quindi la pagina sotto deve restare viva: si apre il fascicolo,
 *    si spunta, si passa al cantiere, e il pannello resta li'.
 *  - `pointerEvents: auto` e `z-[70]`. Finche' una finestra Radix e' aperta il
 *    `<body>` ha i tocchi spenti e le finestre stanno a z-50: senza queste
 *    due righe il pannello si vedrebbe ma non risponderebbe, proprio mentre si
 *    e' dentro il fascicolo a mettere via i documenti.
 *  - Il `pointerdown` si ferma qui. Radix chiude la finestra quando sente un
 *    tocco fuori dal suo contenuto, e questo pannello e' fuori: senza
 *    fermarlo, aprire il raccoglitore dal fascicolo di un dipendente
 *    chiuderebbe il fascicolo.
 */
export default function Raccoglitore() {
  const voci = useRaccoglitore();
  const [aperto, setAperto] = useState(false);
  const [escluse, setEscluse] = useState(() => new Set());
  const [nome, setNome] = useState("");
  const [inCorso, setInCorso] = useState(false);

  // Le voci spuntate: si parte con tutte dentro, e si tolgono quelle che in
  // questo giro non servono. E' la «selezione parziale».
  const scelte = useMemo(
    () => voci.filter((v) => !escluse.has(chiaveVoce(v))), [voci, escluse]);

  const gruppi = useMemo(() => {
    const m = new Map();
    for (const v of voci) {
      const g = v.gruppo || "Documenti";
      m.set(g, [...(m.get(g) || []), v]);
    }
    return [...m.entries()];
  }, [voci]);

  // Il nome della cartella: se il raccoglitore parla di una persona sola, è
  // il suo. Resta modificabile, perché «Pacchetto formazione Rossi» lo scrive
  // chi lo manda, non il programma.
  const nomeProposto = gruppi.length === 1 ? gruppi[0][0] : "Documenti SGM";
  const nomeFinale = (nome.trim() || nomeProposto);

  const spunta = (k) => setEscluse((vecchie) => {
    const n = new Set(vecchie);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });
  const tuttiDentro = () => setEscluse(new Set());
  const tuttiFuori = () => setEscluse(new Set(voci.map(chiaveVoce)));

  const scarica = async () => {
    if (!scelte.length) { toast.error("Non hai spuntato niente da scaricare"); return; }
    setInCorso(true);
    try {
      const r = await authFetch("/raccoglitore/zip", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          voci: scelte.map((v) => ({ tipo: v.tipo, id: v.id })),
          nome: nomeFinale,
        }),
      });
      if (!r.ok) {
        let msg = "Errore nel download";
        try { msg = (await r.json()).detail || msg; } catch { /* niente */ }
        throw new Error(msg);
      }
      const cd = r.headers.get("content-disposition") || "";
      const nomeFile = /filename="?([^";]+)"?/.exec(cd)?.[1] || "documenti.zip";
      const u = window.URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = u; a.download = nomeFile; a.click();
      window.URL.revokeObjectURL(u);
      const mancanti = Number(r.headers.get("X-Mancanti") || 0);
      toast.success(`Scaricati ${r.headers.get("X-Documenti") || scelte.length} documenti`, {
        description: mancanti
          ? `${mancanti} non erano disponibili: sono elencati dentro l'archivio`
          : nomeFile,
      });
    } catch (e) {
      toast.error(e.message || "Errore nel download");
    } finally { setInCorso(false); }
  };

  // «Invia»: la posta parte dal programma di posta di chi lavora, con oggetto
  // e testo già scritti. L'allegato lo aggiunge lui, perché il file è appena
  // stato scaricato ed è sul suo computer: il gestionale non spedisce
  // documenti del personale per conto di nessuno.
  const invia = () => {
    const righe = scelte.map((v) => `- ${v.gruppo ? `${v.gruppo} · ` : ""}${v.titolo}`);
    const corpo = [
      "Buongiorno,",
      "",
      `in allegato ${nomeFinale}:`,
      ...righe,
      "",
      "Cordiali saluti",
      "SGM Impianti srl",
    ].join("\n");
    window.location.href =
      `mailto:?subject=${encodeURIComponent(nomeFinale)}&body=${encodeURIComponent(corpo)}`;
  };

  const svuota = () => {
    svuotaRaccoglitore();
    setEscluse(new Set());
    setNome("");
  };

  const linguetta = (
    <button type="button" onClick={() => setAperto((x) => !x)}
            onPointerDown={(e) => e.stopPropagation()}
            data-testid="apri-raccoglitore"
            style={{ pointerEvents: "auto" }}
            title="Raccoglitore documenti"
            className={`fixed right-0 top-1/3 z-[70] rounded-l-lg shadow-lg border border-r-0 px-2 py-3
                        flex flex-col items-center gap-1 transition-colors
                        ${voci.length
                          ? "bg-orange-500 border-orange-500 text-white hover:bg-orange-600"
                          : "bg-white border-slate-200 text-slate-500 hover:text-slate-800"}`}>
      <Paperclip className="w-4 h-4" />
      <span className="text-[11px] font-bold leading-none">{voci.length}</span>
    </button>
  );

  const pannello = (
    <div data-testid="pannello-raccoglitore"
         onPointerDown={(e) => e.stopPropagation()}
         style={{ pointerEvents: "auto" }}
         className="fixed inset-y-0 right-0 z-[70] w-full sm:w-[380px] bg-white border-l border-slate-200
                    shadow-2xl flex flex-col">
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-200">
        <FolderOpen className="w-4 h-4 text-orange-500" />
        <span className="font-display font-bold">Raccoglitore</span>
        <span className="text-xs text-slate-500">{voci.length} document{voci.length === 1 ? "o" : "i"}</span>
        <Button variant="outline" size="sm" className="h-7 w-7 p-0 ml-auto" onClick={() => setAperto(false)}
                data-testid="chiudi-raccoglitore" title="Chiudi">
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>

      {voci.length === 0 ? (
        <div className="flex-1 flex items-center justify-center p-6 text-center">
          <p className="text-sm text-slate-500">
            Il raccoglitore è vuoto.<br />
            Gira per l&apos;app e premi la <Paperclip className="w-3.5 h-3.5 inline mx-0.5" /> di fianco
            ai documenti che ti servono: fascicoli, cantieri, mezzi, documenti aziendali.
            Poi torna qui e li scarichi tutti insieme.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-1 px-2 py-1.5 border-b border-slate-100 flex-wrap">
            <Button variant="outline" size="sm" className="h-7 text-xs px-2" onClick={tuttiDentro}
                    data-testid="racc-tutti">
              <CheckSquare className="w-3.5 h-3.5 mr-1" /> Seleziona tutto
            </Button>
            <Button variant="outline" size="sm" className="h-7 text-xs px-2" onClick={tuttiFuori}
                    data-testid="racc-nessuno">
              <Square className="w-3.5 h-3.5 mr-1" /> Deseleziona
            </Button>
            <Button variant="outline" size="sm" className="h-7 text-xs px-2 text-red-600 ml-auto"
                    onClick={svuota} data-testid="racc-svuota">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Svuota
            </Button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto p-2 space-y-3">
            {gruppi.map(([gruppo, righe]) => (
              <div key={gruppo}>
                <div className="text-[11px] uppercase tracking-widest text-slate-400 px-1 mb-1">
                  {gruppo}
                </div>
                <div className="space-y-1">
                  {righe.map((v) => {
                    const k = chiaveVoce(v);
                    const dentro = !escluse.has(k);
                    return (
                      <div key={k} data-testid={`racc-voce-${v.id}`}
                           className={`flex items-start gap-2 px-2 py-1.5 border rounded
                                       ${dentro ? "border-slate-200" : "border-slate-100 bg-slate-50 opacity-60"}`}>
                        <input type="checkbox" checked={dentro} onChange={() => spunta(k)}
                               className="mt-0.5 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold leading-tight break-words">{v.titolo}</div>
                          <div className="text-[11px] text-slate-500">
                            {[DA_DOVE[v.tipo] || v.tipo, v.sottotitolo].filter(Boolean).join(" · ")}
                          </div>
                        </div>
                        <button type="button" onClick={() => togliDalRaccoglitore(k)}
                                title="Togli dal raccoglitore"
                                className="text-slate-400 hover:text-red-600 flex-shrink-0">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-slate-200 p-2.5 space-y-2">
            <div>
              <label className="text-[11px] text-slate-500">Nome della cartella</label>
              <Input value={nome} onChange={(e) => setNome(e.target.value)}
                     placeholder={nomeProposto} className="h-8 text-sm"
                     data-testid="racc-nome" />
            </div>
            <Button onClick={scarica} disabled={!scelte.length || inCorso}
                    data-testid="racc-scarica"
                    className={`w-full ${scelte.length
                      ? "bg-orange-500 hover:bg-orange-600 text-white"
                      : "bg-slate-100 text-slate-400 hover:bg-slate-100"}`}>
              <Download className="w-4 h-4 mr-2" />
              {inCorso ? "Preparo la cartella..." : `Scarica ${scelte.length} document${scelte.length === 1 ? "o" : "i"}`}
            </Button>
            {scelte.length !== voci.length && (
              <p className="text-[11px] text-amber-700">
                Ne scarichi {scelte.length} dei {voci.length}: gli altri restano nel raccoglitore.
              </p>
            )}
            <Button variant="outline" onClick={invia} disabled={!scelte.length} className="w-full h-9"
                    data-testid="racc-invia">
              <Mail className="w-4 h-4 mr-2" /> Prepara l&apos;email
            </Button>
            <p className="text-[11px] text-slate-500">
              L&apos;email si apre già scritta con l&apos;elenco: allega la cartella appena scaricata.
              Il raccoglitore si svuota da solo alla disconnessione.
            </p>
          </div>
        </>
      )}
    </div>
  );

  return createPortal(
    <>
      {!aperto && linguetta}
      {aperto && pannello}
    </>,
    document.body);
}
